import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AdminLayout } from '../../components/Layout/AdminLayout';
import { PageHeader } from '../../components/common';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
  Typography,
  Autocomplete,
  Chip,
  InputAdornment,
  Tooltip,
  IconButton,
} from '@mui/material';
import { Save, Cancel, ContentCopy, AutoFixHigh } from '@mui/icons-material';
import { generateVerifyToken } from '../../utils/tokens';
import { useCreateTouchPoint, useCounties } from '../../hooks';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { superAdminService } from '../../services/superadmin.service';
import { colors } from '../../theme/theme';

const CHANNEL_LABELS: Record<string, string> = {
  portal: 'Citizen Portal',
  mobile: 'Mobile App',
  whatsapp: 'WhatsApp',
};

export const AddTouchPointPage = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const createTouchPoint = useCreateTouchPoint();
  // Fetch all onboarded counties at once (backend already filters by whereHas('countyDetails')).
  const { counties } = useCounties(1, 200);

  const [formData, setFormData] = useState({
    touch_point: '',
    county_id: '',
    status: 'active',
    // WhatsApp-only fields
    display_phone_number: '',
    meta_phone_number_id: '',
    meta_waba_id: '',
    meta_access_token: '',
    meta_app_secret: '',
    meta_verify_token: '',
  });

  const isWhatsApp = formData.touch_point === 'whatsapp';

  // After a county is picked, fetch its current touchpoints. The endpoint returns every
  // supported channel with an `exists` flag, so we can show only the ones that haven't
  // already been provisioned for this county.
  const countyTouchpointsQuery = useQuery({
    queryKey: ['superadmin', 'county', formData.county_id, 'touchpoints'],
    queryFn: () => superAdminService.getCountyTouchpoints(Number(formData.county_id)),
    enabled: !!formData.county_id,
  });

  /**
   * For each supported channel, classify it so the dropdown can show:
   *   - 'new': can create from scratch (default behavior).
   *   - 'configure': touchpoint row exists but is incomplete (e.g. WhatsApp row with no Meta
   *     credentials — happens when the onboarding wizard enabled the channel but the superadmin
   *     hasn't filled in Meta creds yet). Selecting routes to the Edit page.
   *   - 'done': already fully configured — hide it.
   */
  type ChannelOption = { channel: string; state: 'new' | 'configure' | 'done'; touchpointId: number | null };
  const channelOptions = useMemo<ChannelOption[]>(() => {
    const rows = (countyTouchpointsQuery.data as any)?.data ?? [];
    if (!Array.isArray(rows) || rows.length === 0) {
      // Not loaded yet — allow all 3 as fresh creates.
      return ['portal', 'mobile', 'whatsapp'].map((c) => ({ channel: c, state: 'new' as const, touchpointId: null as number | null }));
    }
    return rows
      .map((r: any) => {
        const tp = r.touchpoint;
        if (!r.exists) {
          return { channel: r.channel, state: 'new' as const, touchpointId: null };
        }
        // Channel-specific "fully configured" rule.
        const isConfigured =
          r.channel === 'whatsapp'
            ? !!tp?.meta_phone_number_id
            : true; // portal/mobile don't need extra config beyond the existence of the row.
        return {
          channel: r.channel,
          state: (isConfigured ? 'done' : 'configure') as 'done' | 'configure',
          touchpointId: tp?.id ?? null,
        };
      })
      .filter((o: any) => o.state !== 'done');
  }, [countyTouchpointsQuery.data]);

  // Currently the form's touch_point dropdown only handles the 'new' case. If the user picks an
  // option flagged 'configure', we redirect them to the Edit page for that touchpoint and bail.
  useEffect(() => {
    if (!formData.touch_point) return;
    const opt = channelOptions.find((o: any) => o.channel === formData.touch_point);
    if (opt && opt.state === 'configure' && opt.touchpointId) {
      navigate(`/superadmin/touch-points/${opt.touchpointId}/edit`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.touch_point, channelOptions]);

  // If the selected channel disappears from the list when the county changes, clear it.
  useEffect(() => {
    if (
      countyTouchpointsQuery.data &&
      formData.touch_point &&
      !channelOptions.find((o: any) => o.channel === formData.touch_point)
    ) {
      setFormData((prev) => ({ ...prev, touch_point: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelOptions, countyTouchpointsQuery.data]);

  const [credentials, setCredentials] = useState<{ username: string; key: string } | null>(null);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    showSnackbar(`${label} copied to clipboard!`, 'success');
  };

  const handleChange = (field: string, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    try {

      const response = await createTouchPoint.mutateAsync({
        ...formData,
        county_id: Number(formData.county_id),
      });


      // apiClient.post returns response.data directly
      // So backend's { status, data, message, credentials } is the response itself
      // Credentials should be at response.credentials
      const creds = (response as any).credentials;

      if (creds && creds.username && creds.key) {
        setCredentials(creds);
        showSnackbar('Touch point created successfully! Please save the credentials.', 'success');
      } else {
        console.warn('No credentials found in response, navigating away');
        console.warn('Response structure:', response);
        showSnackbar('Touch point created successfully', 'success');
        navigate('/superadmin/touch-points');
      }
    } catch (error: any) {
      console.error('Create error:', error);
      console.error('Error response:', error.response?.data);
      const errorData = error.response?.data;
      if (errorData?.errors) {
        setErrors(errorData.errors);
      }
      const errorMessage = errorData?.message || 'Failed to create touch point';
      showSnackbar(errorMessage, 'error');
    }
  };

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="Add Touch Point"
          subtitle="Create a new service delivery point"
          breadcrumbs={[
            { label: 'Dashboard', path: '/superadmin/dashboard' },
            { label: 'Touch Points', path: '/superadmin/touch-points' },
            { label: 'Add' },
          ]}
        />

        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${colors.grey[200]}`,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          }}
        >
          <CardContent sx={{ p: 4 }}>
            {Object.keys(errors).length > 0 && (
              <Alert severity="error" sx={{ mb: 3 }}>
                Please fix the errors below:
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                  {Object.entries(errors).map(([field, messages]) => (
                    <li key={field}>
                      {Array.isArray(messages) ? messages.join(', ') : messages}
                    </li>
                  ))}
                </ul>
              </Alert>
            )}

            {credentials ? (
              <Box>
                <Alert severity="success" sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Touch Point Created Successfully!
                  </Typography>
                  <Typography variant="body2" paragraph>
                    Please save these credentials. The API key will not be shown again.
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'rgba(0,0,0,0.05)',
                      borderRadius: 1,
                      fontFamily: 'monospace',
                      mt: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">
                        <strong>Username:</strong> {credentials.username}
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<ContentCopy />}
                        onClick={() => handleCopy(credentials.username, 'Username')}
                      >
                        Copy
                      </Button>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ wordBreak: 'break-all', mr: 2 }}>
                        <strong>API Key:</strong> {credentials.key}
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<ContentCopy />}
                        onClick={() => handleCopy(credentials.key, 'API Key')}
                      >
                        Copy
                      </Button>
                    </Box>
                  </Box>
                </Alert>
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Button
                    variant="contained"
                    onClick={() => navigate('/superadmin/touch-points')}
                    sx={{
                      background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.dark} 100%)`,
                    }}
                  >
                    Go to Touch Points
                  </Button>
                </Stack>
              </Box>
            ) : (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  {/* County first — channel options depend on which county is picked */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Autocomplete
                      options={counties?.data ?? []}
                      getOptionLabel={(opt: any) => opt?.name ?? ''}
                      isOptionEqualToValue={(opt: any, val: any) => opt?.id === val?.id}
                      value={(counties?.data ?? []).find((c: any) => c.id === Number(formData.county_id)) ?? null}
                      onChange={(_, value: any) => handleChange('county_id', value?.id ?? '')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required label="County"
                          error={!!errors.county_id}
                          helperText={errors.county_id || 'Only counties that have completed onboarding are listed'}
                        />
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <Autocomplete
                      options={channelOptions.map((o: any) => o.channel)}
                      getOptionLabel={(opt) => CHANNEL_LABELS[opt as string] ?? (opt as string)}
                      value={formData.touch_point || null}
                      onChange={(_, value) => handleChange('touch_point', (value as string) ?? '')}
                      disabled={!formData.county_id || countyTouchpointsQuery.isLoading}
                      renderOption={(props, option) => {
                        const opt = channelOptions.find((o: any) => o.channel === option);
                        const label = CHANNEL_LABELS[option as string] ?? (option as string);
                        return (
                          <Box component="li" {...props}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }}>
                              <span>{label}</span>
                              {opt?.state === 'configure' && (
                                <Chip
                                  size="small" color="warning"
                                  label="Needs configuration"
                                  sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                                />
                              )}
                            </Stack>
                          </Box>
                        );
                      }}
                      noOptionsText={
                        !formData.county_id
                          ? 'Pick a county first'
                          : 'All channels are already fully configured for this county'
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required label="Touch Point Type"
                          error={!!errors.touch_point}
                          helperText={
                            errors.touch_point ||
                            (formData.county_id
                              ? 'Channels marked "Needs configuration" already exist and will open in edit mode'
                              : 'Select a county to see available channels')
                          }
                        />
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={formData.status}
                        label="Status"
                        onChange={(e) => handleChange('status', e.target.value)}
                      >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {isWhatsApp && (
                    <>
                      <Grid size={{ xs: 12 }}>
                        <Alert severity="info" sx={{ mb: 1 }}>
                          <Typography variant="body2" fontWeight={600} gutterBottom>
                            Configure this number in Meta first
                          </Typography>
                          <Typography variant="body2">
                            In your Meta App → WhatsApp → Configuration, set:<br />
                            <strong>Callback URL:</strong> <code>{`${window.location.protocol}//${window.location.hostname}/api/whatsapp/webhook`}</code><br />
                            <strong>Verify token:</strong> whatever you enter below (must match exactly).
                          </Typography>
                        </Alert>
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth required label="Meta Phone Number ID"
                          value={formData.meta_phone_number_id}
                          onChange={(e) => handleChange('meta_phone_number_id', e.target.value)}
                          error={!!errors.meta_phone_number_id}
                          helperText={errors.meta_phone_number_id || 'From Meta → WhatsApp → API Setup'}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth label="Display Phone Number"
                          value={formData.display_phone_number}
                          onChange={(e) => handleChange('display_phone_number', e.target.value)}
                          placeholder="+254700000000"
                          helperText="The number citizens will see"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth label="WhatsApp Business Account ID"
                          value={formData.meta_waba_id}
                          onChange={(e) => handleChange('meta_waba_id', e.target.value)}
                          helperText="Optional — from Meta → WhatsApp → API Setup"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth required label="Webhook Verify Token"
                          value={formData.meta_verify_token}
                          onChange={(e) => handleChange('meta_verify_token', e.target.value)}
                          error={!!errors.meta_verify_token}
                          helperText={errors.meta_verify_token || 'Generate one and paste the same value into Meta — they must match'}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Tooltip title="Generate random token">
                                  <IconButton
                                    size="small"
                                    edge="end"
                                    onClick={() => handleChange('meta_verify_token', generateVerifyToken())}
                                  >
                                    <AutoFixHigh fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {formData.meta_verify_token && (
                                  <Tooltip title="Copy">
                                    <IconButton
                                      size="small"
                                      edge="end"
                                      onClick={() => handleCopy(formData.meta_verify_token, 'Verify token')}
                                    >
                                      <ContentCopy fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth required label="Access Token" multiline minRows={2}
                          value={formData.meta_access_token}
                          onChange={(e) => handleChange('meta_access_token', e.target.value)}
                          error={!!errors.meta_access_token}
                          helperText={errors.meta_access_token || 'System User token (long-lived). Stored encrypted.'}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth required label="App Secret"
                          type="password"
                          value={formData.meta_app_secret}
                          onChange={(e) => handleChange('meta_app_secret', e.target.value)}
                          error={!!errors.meta_app_secret}
                          helperText={errors.meta_app_secret || 'From Meta App → Settings → Basic. Used to verify webhook signatures. Stored encrypted.'}
                        />
                      </Grid>
                    </>
                  )}

                  <Grid size={{ xs: 12 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Username will be auto-generated as: <strong>county-touchpoint</strong> (e.g., nairobi-whatsapp).
                      An API key will be generated automatically (shown once).
                    </Alert>
                  </Grid>

                <Grid size={{ xs: 12 }}>
                  <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      startIcon={<Cancel />}
                      onClick={() => navigate('/superadmin/touch-points')}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={<Save />}
                      disabled={createTouchPoint.isPending}
                      sx={{
                        background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.dark} 100%)`,
                      }}
                    >
                      {createTouchPoint.isPending ? 'Creating...' : 'Create Touch Point'}
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </form>
            )}
          </CardContent>
        </Card>
      </Box>
    </AdminLayout>
  );
};
